import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
// Styles
import Wrapper, { Navigation, CustomControls } from './styles';
import Button from '../Button';
import Flex from '../Flex';
import Spacer from '../Spacer';
import Tracks from '../Tracks';

interface Props {
  className?: string;
  doneText?: string;
  submitText?: string;
  isSubmissible?: boolean;
  isValidating?: boolean;
  jumpToNext?: boolean;
  onCancel?: () => void;
  onDone?: () => void;
  onSubmit?: () => void;
  onNext?: (id: string) => void;
  onChange?: (id: string) => void;
  showNavigation?: boolean;
  customControls?: React.ReactNode;
  showNavigationTop?: boolean;
  clickEnabled?: boolean;
  steps: {
    name: string;
    component: React.ReactNode;
    id: string;
    validateOnNext?: boolean;
  }[];
}

export const getNavStyles = (index: number, length: number) => {
  let styles = [];

  for (let i = 0; i < length; i++) {
    if (i < index) {
      styles.push('-done');
    } else if (i === index) {
      styles.push('-doing');
    } else {
      styles.push('-todo');
    }
  }

  return styles;
};

const getButtonsState = (index: number, length: number) => {
  if (index > 0 && index < length - 1) {
    return {
      showDoneBtn: false,
      showSubmitBtn: false,
      showPreviousBtn: true,
      showNextBtn: true,
    };
  } else if (index === 0) {
    return {
      showDoneBtn: false,
      showSubmitBtn: false,
      showPreviousBtn: false,
      showNextBtn: true,
    };
  } else {
    return {
      showDoneBtn: true,
      showSubmitBtn: true,
      showPreviousBtn: true,
      showNextBtn: false,
    };
  }
};

/**
 * @render react
 * @name MultiStep component
 * @description MultiStep component.
 * @todo Make code more readable
 * @example
 * <MultiStep
 *    steps={[
 *      { name: 'Name', component: <Step /> },
 *      { name: 'Email', component: <Step /> }
 *    ]}
 * />
 */

const MultiStep: FC<Props> = ({
  clickEnabled,
  className,
  doneText,
  submitText,
  isSubmissible,
  isValidating,
  jumpToNext,
  onCancel: handleCancel,
  onDone: handleDone,
  onSubmit: handleSubmit,
  onNext: handleNext,
  onChange: handleChange,
  showNavigation,
  showNavigationTop,
  customControls,
  steps,
}) => {
  const [styles, setStyles] = useState(getNavStyles(0, steps.length));
  const [compState, setComp] = useState(0);
  const [buttonsState, setButtons] = useState(getButtonsState(0, steps.length));
  const [previousClicked, setPreviousClicked] = useState(false);

  const setStepState = (index: number) => {
    setStyles(getNavStyles(index, steps.length));
    setComp(index < steps.length ? index : compState);
    setButtons(getButtonsState(index, steps.length));
  };

  const next = (stepsLength?: number) => {
    if (handleNext) {
      handleNext(steps[compState].id);

      if (steps[compState].validateOnNext) {
        return;
      }

      setStepState(compState + 1);
    }
  };

  const previous = () => setStepState(compState > 0 ? compState - 1 : compState);

  const handleClick = (event: React.BaseSyntheticEvent) => {
    if (clickEnabled) {
      if (event.currentTarget.value === steps.length - 1 && compState === steps.length - 1) {
        setStepState(steps.length);
      } else {
        setStepState(event.currentTarget.value);
      }
    }
  };

  const renderNavigation = () => {
    return (
      showNavigation && (
        <Flex direction="row" justify="flex-end" size="1">
          <Navigation>
            {buttonsState.showPreviousBtn && (
              <Button
                onClick={() => {
                  previous();
                  setPreviousClicked(true);
                }}
                text="Previous"
                type="button"
              />
            )}
            <Button ml={4} onClick={() => handleCancel && handleCancel()} text="Cancel" type="button" />
            {buttonsState.showNextBtn && (
              <Button
                ml={4}
                onClick={() => {
                  setPreviousClicked(false);
                  next();
                }}
                text="Next"
                type="button"
                isLoading={isValidating}
              />
            )}
            {buttonsState.showDoneBtn && (
              <Button
                ml={4}
                onClick={() => handleDone && handleDone()}
                text={doneText}
                type="button"
                isLoading={isValidating}
              />
            )}
            {buttonsState.showSubmitBtn && isSubmissible && (
              <Button
                ml={4}
                onClick={() => handleSubmit && handleSubmit()}
                text={submitText}
                type="button"
                isLoading={isValidating}
              />
            )}
          </Navigation>
        </Flex>
      )
    );
  };

  const renderCustomControls = () => {
    return (
      !!customControls && (
        <Flex direction="row" justify="flex-start" size="1">
          <CustomControls>{customControls}</CustomControls>
        </Flex>
      )
    );
  };

  useEffect(() => {
    if (jumpToNext && !previousClicked) {
      setStepState(compState + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToNext, compState]);

  useEffect(() => {
    if (handleChange) {
      handleChange(steps[compState].id);
    }
  }, [compState, handleChange, steps]);

  return (
    <Wrapper className={classNames('c-multiStep', className)}>
      <Tracks clickEnabled={false} onClick={handleClick} steps={steps} styles={styles} />
      {showNavigationTop && renderNavigation()}
      {steps[compState].component}
      <Spacer spacing={40} />
      <Flex direction="row" justify="space-between" size="1">
        {renderCustomControls()}
        {!showNavigationTop && renderNavigation()}
      </Flex>
    </Wrapper>
  );
};

MultiStep.defaultProps = {
  doneText: 'Done',
  isValidating: false,
  jumpToNext: false,
  onCancel: () => null,
  onDone: () => null,
  onNext: () => null,
  showNavigation: true,
  showNavigationTop: false,
  clickEnabled: true,
};

export default MultiStep;
